// TODO use only the need parts of d3 instead of importing the whole library, which will reduce bundle size and improve performance
import * as d3module from 'd3';
// eslint-disable-next-line import-x/default
import d3tip from 'd3-tip';

import { defu } from 'defu';
import {
  createWellTextures,
  type WellTextures,
} from './configs/render.textures';

const d3 = { ...d3module, tip: d3tip };

import {
  isWellEmpty,
  parseFormatVersion,
  type Constructive,
  type Lithology,
  type Units,
  type Well,
} from '@welldot/core';
import {
  formatNumber,
  getCurrentPump,
  getProfileLastItemsDepths,
} from '@welldot/utils';
import {
  ComponentsClassNames,
  DeepPartial,
  DrawContext,
  DrawGroups,
  Highlights,
  InstanceState,
  RenderableWell,
  RenderConfig,
  SvgInstance,
  SvgSelection,
  WellTheme,
} from '~/types/render.types';
import { getLengthUnit } from '~/utils/format.utils';
import { DEFAULT_COMPONENTS_CLASS_NAMES } from './configs/render.classnames';
import {
  DEFAULT_WELL_THEME,
  INTERACTIVE_RENDER_CONFIG,
} from './configs/render.configs';
import { drawAnnotationLabels } from './renderers/annotation-labels.renderer';
import { drawCaves } from './renderers/caves.renderer';
import { drawCentralizers } from './renderers/centralizers.renderer';
import { drawColumnHeaders } from './renderers/column-headers.renderer';
import { drawConstructionLabels } from './renderers/construction-labels.renderer';
import {
  drawCementPad,
  drawConstructive,
  layoutConstruction,
} from './renderers/construction.renderer';
import { drawFractures } from './renderers/fractures.renderer';
import { drawHighlights } from './renderers/highlights.renderer';
import {
  drawWellLegend,
  drawWellLegendSymbol,
  getWellLegendItems,
  WellLegendItem,
} from './renderers/legend.renderer';
import {
  drawLithology,
  drawLithologySwatch,
} from './renderers/lithology.renderer';
import { drawMarkers } from './renderers/markers.renderer';
import { drawOverlap } from './renderers/overlap.renderer';
import { drawOverview } from './renderers/overview.renderer';
import { drawPump } from './renderers/pump.renderer';
import { drawUnitLabels } from './renderers/unit-labels.renderer';
import { asSvgElement } from './utils/d3.utils';
import { getProfileTopDepth, resolvePanelWindows } from './utils/layout.utils';
import { buildSvgStyleBlock } from './utils/render.styles';
import {
  filterByDepth,
  fractureDip,
  populateTooltips,
  preloadFgdcTextures,
} from './utils/render.utils';

/**
 * SVG-based renderer for geological well profiles.
 *
 * One instance manages one or more SVG panels (multi-panel layouts for long wells).
 * Call `prepareSvg()` once to initialise the DOM and preload textures, then call
 * `draw(profile)` whenever the profile data changes.
 *
 * @example
 * ```ts
 * const renderer = new WellRenderer(
 *   [{ selector: '#well-svg', height: 600, width: 300, margins: { top: 20, right: 10, bottom: 20, left: 40 } }],
 *   { renderConfig: INTERACTIVE_RENDER_CONFIG },
 * );
 * await renderer.prepareSvg();
 * renderer.draw(profile);
 * ```
 */
export class WellRenderer {
  private svgInstances: SvgInstance[] = [];
  private instanceStates: InstanceState[] = [];
  private textures: WellTextures = createWellTextures();
  private onError?: (err: Error) => void;
  private onZoom?: (scale: number) => void;

  classes = DEFAULT_COMPONENTS_CLASS_NAMES;
  private renderConfig: RenderConfig = INTERACTIVE_RENDER_CONFIG;
  private theme: WellTheme = DEFAULT_WELL_THEME;
  private units: Units = {
    length: 'm',
    diameter: 'mm',
  };
  private locale: 'en' | 'pt' = 'pt';

  /**
   * @param svgs - One entry per SVG panel; multi-entry for split-panel layouts.
   * @param options.classNames - Override default BEM CSS class names.
   * @param options.units - Active measurement units (`length` and `diameter`).
   * @param options.locale - Locale for renderer-drawn text (diameter symbol, and any `RenderConfig` fields resolved via `applyRenderLocale`). Defaults to `'pt'`, matching the package's historical output.
   * @param options.renderConfig - Rendering behaviour (zoom, pan, animation, layout). Defaults to `INTERACTIVE_RENDER_CONFIG`.
   * @param options.theme - Visual theme overrides. Merged on top of `DEFAULT_WELL_THEME`.
   * @param options.onError - Called when a draw error occurs (e.g., a renderer throws). Falls back to `console.error` if omitted.
   * @param options.onZoom - Called with the current zoom scale (`1` = initial/fit) whenever the user zooms or pans, and whenever `zoomBy`/`resetZoom` are called. No-op if `renderConfig.zoom`/`pan` are both disabled.
   */
  constructor(
    svgs: SvgInstance[],
    options: {
      classNames?: DeepPartial<ComponentsClassNames>;
      units?: Units;
      locale?: 'en' | 'pt';
      renderConfig?: DeepPartial<RenderConfig>;
      theme?: DeepPartial<WellTheme>;
      onError?: (err: Error) => void;
      onZoom?: (scale: number) => void;
    } = {},
  ) {
    if (svgs.length === 0) {
      console.warn(
        '[WellRenderer] No SVG instances provided. The renderer will be a no-op.',
      );
      return;
    }
    this.svgInstances = svgs;
    if (options.onError) this.onError = options.onError;
    if (options.onZoom) this.onZoom = options.onZoom;

    if (options.classNames) {
      this.classes = defu(
        options.classNames,
        DEFAULT_COMPONENTS_CLASS_NAMES,
      ) as ComponentsClassNames;
    }

    if (options.units) {
      this.units = { ...this.units, ...options.units };
    }

    if (options.locale) {
      this.locale = options.locale;
    }

    if (options.renderConfig) {
      this.renderConfig = defu(
        options.renderConfig,
        INTERACTIVE_RENDER_CONFIG,
      ) as RenderConfig;
    }

    this.theme = defu(options.theme ?? {}, DEFAULT_WELL_THEME) as WellTheme;

    this.textures = createWellTextures(this.renderConfig.textures);
  }

  private initInstanceSvg(inst: SvgInstance, index: number): InstanceState {
    const clipId = `fractures-clip-${index}`;
    const clipRectId = `fractures-clip-rect-${index}`;
    const { selector, height, width, margins } = inst;
    const svgWidth = width + margins.left + margins.right;
    const svgHeight = height + margins.top + margins.bottom;

    const svg = d3.select(selector) as SvgSelection;
    svg.selectAll('*').remove();

    svg.attr('height', svgHeight).attr('width', svgWidth);

    const defs = svg.append('defs');
    defs.append('style').text(buildSvgStyleBlock(this.theme));
    defs
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('id', clipRectId)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 100000)
      .attr('height', 10000);

    svg
      .append('g')
      .attr('class', this.classes.overview.group)
      .attr('transform', `translate(0, ${margins.top})`);
    svg.append('g').attr('class', this.classes.columnHeaders.group);

    const poco = svg.append('g').attr('class', this.classes.wellGroup);

    const geologic = poco
      .append('g')
      .attr('class', this.classes.geologicGroup)
      .attr('transform', `translate(${margins.left}, ${margins.top})`);

    geologic.append('g').attr('class', this.classes.yAxis);
    geologic.append('g').attr('class', this.classes.unitLabels.group);
    geologic.append('g').attr('class', this.classes.lithology.group);
    geologic.append('g').attr('class', this.classes.caves.group);
    geologic.append('g').attr('class', this.classes.labels.lithology.group);
    geologic.append('g').attr('class', this.classes.highlights.geologicGroup);

    const construction = poco
      .append('g')
      .attr('class', this.classes.constructionGroup)
      .attr(
        'transform',
        `translate(${margins.left + width / 2}, ${margins.top})`,
      );

    const fracturesGroup = poco
      .append('g')
      .attr('class', this.classes.fractures.group)
      .attr('transform', `translate(${margins.left}, ${margins.top})`)
      .attr('clip-path', `url(#${clipId})`);

    fracturesGroup
      .append('g')
      .attr('class', this.classes.highlights.fracturesGroup);

    poco
      .append('g')
      .attr('class', this.classes.markers.group)
      .attr('transform', `translate(${margins.left}, ${margins.top})`);

    construction.append('g').attr('class', this.classes.cementPad.group);
    construction.append('g').attr('class', this.classes.boreHole.group);
    construction.append('g').attr('class', this.classes.surfaceCase.group);
    construction.append('g').attr('class', this.classes.holeFill.group);
    construction.append('g').attr('class', this.classes.wellCase.group);
    construction.append('g').attr('class', this.classes.wellScreen.group);
    construction.append('g').attr('class', this.classes.reduction.group);
    construction.append('g').attr('class', this.classes.pump.group);
    construction.append('g').attr('class', this.classes.centralizer.group);
    construction.append('g').attr('class', this.classes.conflict.group);
    construction
      .append('g')
      .attr('class', this.classes.constructionLabels.group);
    construction
      .append('g')
      .attr('class', this.classes.highlights.constructionGroup);

    // Last child of the well group, so the band hatches over everything.
    poco
      .append('g')
      .attr('class', this.classes.overlap.group)
      .attr('transform', `translate(${margins.left}, ${margins.top})`);

    Object.values(this.textures).forEach(texture => svg.call(texture));

    return {
      svg,
      height,
      width,
      margins,
      clipId,
      clipRectId,
      depthRange: inst.depthRange,
      repeatedTo: inst.repeatedTo,
    };
  }

  /** Renders a standalone legend into the element matched by `selector`. */
  public renderLegend(selector: string, profile: Well): void {
    drawWellLegend(selector, profile, {
      config: this.renderConfig.legend,
      theme: this.theme,
      classNames: this.classes.legend,
      textures: this.renderConfig.textures,
    });
  }

  /** Lists the legend entries `profile` needs, labeled from `renderConfig.legend.labels`. */
  public getLegendItems(profile: Well): WellLegendItem[] {
    return getWellLegendItems(profile, this.renderConfig.legend.labels);
  }

  /**
   * Renders one legend entry's symbol (see {@link getLegendItems}) into its
   * own SVG matched by `selector`, with the textures it needs defined inside.
   */
  public renderLegendSymbol(selector: string, item: WellLegendItem): void {
    drawWellLegendSymbol(selector, item, {
      theme: this.theme,
      classNames: this.classes.legend,
      textures: this.renderConfig.textures,
    });
  }

  /**
   * Renders one lithology layer's fill as a `size`×`size` square into its
   * own SVG matched by `selector`, with its texture defined inside (e.g. for
   * a swatch in a PDF table). Call after `prepareSvg()`, which loads the
   * FGDC textures.
   */
  public renderLithologySwatch(
    selector: string,
    layer: Lithology,
    size = 10,
  ): void {
    drawLithologySwatch(selector, layer, { size, theme: this.theme });
  }

  /**
   * Multiplies the current zoom scale by `factor` (e.g. `1.25` to zoom in,
   * `1 / 1.25` to zoom out) on every panel. No-op if both `renderConfig.zoom`
   * and `renderConfig.pan` are disabled (no zoom behavior was attached).
   */
  public zoomBy(factor: number): void {
    for (const state of this.instanceStates) {
      if (!state.zoomNode) continue;
      asSvgElement<SVGSVGElement>(state.svg)
        .transition()
        .call(state.zoomNode.scaleBy, factor);
    }
  }

  /**
   * Resets zoom/pan on every panel back to `renderConfig.zoomLevel`
   * (defaults to `1`, i.e. the initial fit-to-container view).
   */
  public resetZoom(): void {
    const target = d3.zoomIdentity.scale(this.renderConfig.zoomLevel ?? 1);
    for (const state of this.instanceStates) {
      if (!state.zoomNode) continue;
      asSvgElement<SVGSVGElement>(state.svg)
        .transition()
        .call(state.zoomNode.transform, target);
    }
  }

  /**
   * Current zoom scale factor of the first panel (`1` = initial/fit).
   * Returns `1` if zoom/pan is disabled or `prepareSvg()` hasn't run yet.
   */
  public getZoomScale(): number {
    const node = this.instanceStates[0]?.svg.node();
    return node ? d3.zoomTransform(node as Element).k : 1;
  }

  /**
   * Removes everything drawn on every panel (layers, labels, axis,
   * highlights) and keeps the structure `prepareSvg()` built, so the next
   * `draw()` needs no new `prepareSvg()`. `draw()` calls it for an empty well.
   */
  public clear(): void {
    const c = this.classes;
    const layerGroups = [
      c.overview.group,
      c.columnHeaders.group,
      c.yAxis,
      c.unitLabels.group,
      c.lithology.group,
      c.caves.group,
      c.labels.lithology.group,
      c.highlights.geologicGroup,
      c.highlights.constructionGroup,
      c.highlights.fracturesGroup,
      c.markers.group,
      c.cementPad.group,
      c.boreHole.group,
      c.surfaceCase.group,
      c.holeFill.group,
      c.wellCase.group,
      c.wellScreen.group,
      c.reduction.group,
      c.pump.group,
      c.centralizer.group,
      c.conflict.group,
      c.constructionLabels.group,
      c.overlap.group,
    ];
    for (const { svg } of this.instanceStates) {
      for (const cls of layerGroups) {
        svg.select(`.${cls}`).selectAll('*').interrupt().remove();
      }
      // The fractures group also holds its highlights subgroup: drop only the items.
      svg
        .select(`.${c.fractures.group}`)
        .selectAll(`g.${c.fractures.item}`)
        .interrupt()
        .remove();
    }
  }

  /**
   * Initialises the SVG DOM structure and preloads FGDC textures.
   * Must be called once before the first `draw()`.
   */
  public async prepareSvg() {
    await preloadFgdcTextures();
    this.instanceStates = this.svgInstances.map((inst, i) =>
      this.initInstanceSvg(inst, i),
    );
  }

  /**
   * Renders the well profile into all configured SVG panels. Layers the
   * profile no longer has are removed; an empty well (`isWellEmpty`) clears
   * the panels (see {@link clear}).
   * @param profile - The well data to render.
   * @param options.units - Override the instance-level measurement units for this render.
   * @param options.locale - Override the instance-level locale for this render.
   * @param options.highlights - Highlight overlays to display on top of the profile.
   */
  draw(
    profile: RenderableWell,
    options: {
      units?: Units;
      locale?: 'en' | 'pt';
      highlights?: Highlights;
    } = {},
  ) {
    if (isWellEmpty(profile)) {
      this.clear();
      return;
    }

    if (parseFormatVersion(profile.version)?.major !== 2) {
      console.warn(
        `[WellRenderer] Received a .well file with version ${profile.version ?? 'unknown'}. ` +
          `Only v2 is supported. Normalize with deserializeWell() from @welldot/core before rendering.`,
      );
    }

    this.units = { ...this.units, ...options.units };
    if (options.locale) {
      this.locale = options.locale;
    }
    const highlights = options.highlights ?? {};

    const maxValues = getProfileLastItemsDepths(profile);
    const maxYValues = d3.max(maxValues) || 0;

    // Explicit `depthRange`s when every panel has one; otherwise the depth is
    // split in proportion to the panel heights, back to back.
    const windows = resolvePanelWindows(
      this.instanceStates,
      maxYValues,
      getProfileTopDepth(profile),
    );
    this.instanceStates.forEach((state, i) => {
      const [from, to] = windows[i]!;
      this.drawLogToInstance(state, profile, from, to, maxYValues, highlights);
    });
  }

  private drawLogToInstance(
    state: InstanceState,
    profile: RenderableWell,
    depthFrom: number,
    depthTo: number,
    totalDepth: number,
    highlights: Highlights = {},
  ) {
    const svg = state.svg;

    const pocoGroup = svg.select(`.${this.classes.wellGroup}`);
    const lithologyGroup = svg.select(`.${this.classes.lithology.group}`);
    const fracturesGroup = svg.select(`.${this.classes.fractures.group}`);
    const cavesGroup = svg.select(`.${this.classes.caves.group}`);
    const lithologyLabelsGroup = svg.select(
      `.${this.classes.labels.lithology.group}`,
    );
    const constructionGroup = svg.select(`.${this.classes.constructionGroup}`);
    const cementPadGroup = svg.select(`.${this.classes.cementPad.group}`);
    const holeGroup = svg.select(`.${this.classes.boreHole.group}`);
    const surfaceCaseGroup = svg.select(`.${this.classes.surfaceCase.group}`);
    const holeFillGroup = svg.select(`.${this.classes.holeFill.group}`);
    const wellCaseGroup = svg.select(`.${this.classes.wellCase.group}`);
    const wellScreenGroup = svg.select(`.${this.classes.wellScreen.group}`);
    const reductionGroup = svg.select(`.${this.classes.reduction.group}`);
    const centralizerGroup = svg.select(`.${this.classes.centralizer.group}`);
    const pumpGroup = svg.select(`.${this.classes.pump.group}`);
    const conflictGroup = svg.select(`.${this.classes.conflict.group}`);
    const constructionLabelsGroup = svg.select(
      `.${this.classes.constructionLabels.group}`,
    );
    const highlightsGeologicGroup = svg.select(
      `.${this.classes.highlights.geologicGroup}`,
    );
    const highlightsConstructionGroup = svg.select(
      `.${this.classes.highlights.constructionGroup}`,
    );
    const highlightsFracturesGroup = svg.select(
      `.${this.classes.highlights.fracturesGroup}`,
    );
    const unitLabelsGroup = svg.select(`.${this.classes.unitLabels.group}`);
    const overviewGroup = svg.select(`.${this.classes.overview.group}`);
    const columnHeadersGroup = svg.select(
      `.${this.classes.columnHeaders.group}`,
    );
    const markersGroup = svg.select(`.${this.classes.markers.group}`);
    const overlapGroup = svg.select(`.${this.classes.overlap.group}`);

    const svgWidth = state.width + state.margins.left + state.margins.right;
    const svgHeight = state.height + state.margins.top + state.margins.bottom;
    const { pocoWidthRatio, pocoCenterRatio } = this.renderConfig.layout;
    const POCO_WIDTH = svgWidth * pocoWidthRatio;
    const POCO_CENTER = svgWidth * pocoCenterRatio;

    const { duration, ease } = this.renderConfig.animation;
    const transition = d3.transition().duration(duration).ease(ease);

    const tooltips = populateTooltips(
      svg,
      this.classes,
      this.units,
      this.renderConfig.tooltips,
      this.renderConfig.tooltipLabels,
      this.locale,
    );

    const { xLeft: geoXLeft, xRightInset: geoXRightInset } =
      this.renderConfig.geologic;
    const geoXRight = svgWidth - geoXRightInset;
    const pocoCenterX = state.width / 2 + POCO_CENTER / 2;

    const geologicData = {
      lithology: profile.lithology,
      fractures: profile.fractures,
      caves: profile.caves,
    };
    const constructionData = {
      cement_pad: profile.cement_pad,
      bore_hole: profile.bore_hole,
      hole_fill: profile.hole_fill,
      surface_case: profile.surface_case,
      well_case: profile.well_case,
      well_screen: profile.well_screen,
      reduction: profile.reduction,
      centralizers: profile.centralizers ?? [],
      fractures: profile.fractures,
    } as Constructive;

    const contentHeight = svgHeight - state.margins.top - state.margins.bottom;

    const yScaleLocal = d3
      .scaleLinear()
      .domain([depthFrom, depthTo])
      .range([0, contentHeight]);

    const depthFactor = this.units.length === 'ft' ? 3.28084 : 1;
    const yScaleAxis = d3
      .scaleLinear()
      .domain([depthFrom * depthFactor, depthTo * depthFactor])
      .range([0, contentHeight]);

    const yAxis = d3.axisLeft(yScaleAxis).tickFormat(
      (d: d3module.NumberValue) =>
        `${formatNumber(Number(d), {
          maximumFractionDigits: this.units.length === 'ft' ? 1 : 2,
          locale: this.locale,
        })}${getLengthUnit(this.units.length)}`,
    );

    const gY = svg.select<SVGGElement>(`.${this.classes.yAxis}`).call(yAxis);

    gY.select('.domain')
      .attr('stroke', this.theme.labels.color)
      .attr('fill', 'none');
    gY.selectAll('.tick line').attr('stroke', this.theme.labels.color);
    gY.selectAll('.tick text')
      .attr('fill', this.theme.labels.color)
      .attr('stroke', 'none')
      .attr(
        'font-size',
        this.theme.labels.scaleFontSize ?? this.theme.labels.fontSize,
      )
      .attr(
        'font-family',
        this.theme.labels.scaleFont ??
          this.theme.labels.bodyFont ??
          'sans-serif',
      );

    type SpanDatum = { from?: number; to?: number; thickness?: number };

    const spanY = (d: SpanDatum): number => {
      if (d.thickness) return yScaleLocal(0) - yScaleLocal(d.thickness);
      return yScaleLocal(Math.max(d.from ?? 0, depthFrom));
    };

    const spanH = (d: SpanDatum): number => {
      if (d.thickness) return yScaleLocal(d.thickness) - yScaleLocal(0);
      return Math.max(
        0,
        yScaleLocal(Math.min(d.to ?? 0, depthTo)) -
          yScaleLocal(Math.max(d.from ?? 0, depthFrom)),
      );
    };

    const groups: DrawGroups = {
      lithologyGroup,
      unitLabelsGroup,
      cavesGroup,
      fracturesGroup,
      lithologyLabelsGroup,
      constructionGroup,
      constructionLabelsGroup,
      cementPadGroup,
      holeGroup,
      surfaceCaseGroup,
      holeFillGroup,
      wellCaseGroup,
      wellScreenGroup,
      reductionGroup,
      centralizerGroup,
      pumpGroup,
      conflictGroup,
      highlightsGeologicGroup,
      highlightsConstructionGroup,
      highlightsFracturesGroup,
      overviewGroup,
      columnHeadersGroup,
      markersGroup,
      overlapGroup,
    };

    const ctx: DrawContext = {
      svg,
      state,
      svgWidth,
      svgHeight,
      POCO_WIDTH,
      POCO_CENTER,
      pocoCenterX,
      geoXLeft,
      geoXRight,
      depthFrom,
      depthTo,
      totalDepth,
      repeatedTo: state.repeatedTo,
      yScale: yScaleLocal,
      transition,
      tooltips,
      renderConfig: this.renderConfig,
      theme: this.theme,
      classes: this.classes,
      textures: this.textures,
      units: this.units,
      locale: this.locale,
      constructionData,
      groups,
    };

    const inDepth = filterByDepth(depthFrom, depthTo);
    const currentPump = getCurrentPump(profile as Well);
    const filteredConstruction = {
      ...constructionData,
      bore_hole: constructionData.bore_hole.filter(inDepth),
      hole_fill: constructionData.hole_fill.filter(inDepth),
      surface_case: constructionData.surface_case.filter(inDepth),
      well_case: constructionData.well_case.filter(inDepth),
      well_screen: constructionData.well_screen.filter(inDepth),
      reduction: constructionData.reduction?.filter(inDepth) ?? [],
      centralizers: constructionData.centralizers?.filter(inDepth) ?? [],
    };

    const zooming = (e: { transform: d3module.ZoomTransform }): void => {
      const transform = e.transform;
      const zoomedCtx: DrawContext = {
        ...ctx,
        yScale: transform.rescaleY(yScaleLocal),
      };

      gY.call(yAxis.scale(transform.rescaleY(yScaleAxis)));

      pocoGroup
        .selectAll('rect')
        .attr('y', (d: unknown) => {
          if (!d) return null;
          return transform.applyY(spanY(d as SpanDatum));
        })
        .attr('height', (d: unknown) => {
          if (!d) return null;
          return transform.k * spanH(d as SpanDatum);
        });

      // Construction outlines, shoes, reductions and the cement pad follow
      // the rescaled y; the pad is paths, so the rect rescale above skips it.
      layoutConstruction(zoomedCtx);
      drawCementPad(zoomedCtx, constructionData.cement_pad);

      // Fractures: recompute transform so rotation pivot tracks the scaled depth
      fracturesGroup
        .selectAll(`g.${this.classes.fractures.item}`)
        .attr('transform', (d: unknown) => {
          if (!d) return null;
          const f = d as { depth: number; dip?: number | null };
          const cx = pocoCenterX;
          const cy = transform.applyY(yScaleLocal(f.depth));
          return `translate(0,${cy}) rotate(${fractureDip(f.dip)},${cx},0)`;
        });

      // Delegated redraws — unconditional, each renderer self-guards:
      drawCaves(zoomedCtx, geologicData.caves?.filter(inDepth) ?? []);
      drawUnitLabels(zoomedCtx, profile.lithology?.filter(inDepth) ?? []);
      drawAnnotationLabels(zoomedCtx, {
        lithology: profile.lithology?.filter(inDepth) ?? [],
        fractures:
          profile.fractures?.filter(
            f => f.depth >= depthFrom && f.depth < depthTo,
          ) ?? [],
        caves: profile.caves?.filter(inDepth) ?? [],
      });
      drawPump(zoomedCtx, currentPump);
      drawCentralizers(zoomedCtx, filteredConstruction);
      drawConstructionLabels(zoomedCtx, {
        well_case: constructionData.well_case.filter(inDepth),
        well_screen: constructionData.well_screen.filter(inDepth),
      });
      drawMarkers(zoomedCtx, {
        lithology: profile.lithology ?? [],
        fractures: profile.fractures ?? [],
      });
      drawOverlap(zoomedCtx);
      drawHighlights(zoomedCtx, highlights);
      this.onZoom?.(transform.k);
    };

    const drawProfile = () => {
      drawLithology(ctx, profile.lithology?.filter(inDepth) ?? []);
      drawUnitLabels(ctx, profile.lithology?.filter(inDepth) ?? []);
      drawAnnotationLabels(ctx, {
        lithology: profile.lithology?.filter(inDepth) ?? [],
        fractures:
          profile.fractures?.filter(
            f => f.depth >= depthFrom && f.depth < depthTo,
          ) ?? [],
        caves: profile.caves?.filter(inDepth) ?? [],
      });
      drawCaves(ctx, profile.caves?.filter(inDepth) ?? []);
      drawFractures(
        ctx,
        profile.fractures?.filter(
          // exclusive upper bound: depthTo is the start of the next panel
          f => f.depth >= depthFrom && f.depth < depthTo,
        ) ?? [],
      );
      drawConstructive(ctx, filteredConstruction);
      drawPump(ctx, currentPump);
      drawCentralizers(ctx, filteredConstruction);
      drawConstructionLabels(ctx, {
        well_case: filteredConstruction.well_case,
        well_screen: filteredConstruction.well_screen,
      });
      drawMarkers(ctx, {
        lithology: profile.lithology ?? [],
        fractures: profile.fractures ?? [],
      });
      drawOverview(ctx, profile.lithology ?? []);
      drawColumnHeaders(ctx);
      drawOverlap(ctx);
      svg.select(`#${state.clipRectId}`).attr('y', 0).attr('height', svgHeight);
      drawHighlights(ctx, highlights);
    };

    try {
      drawProfile();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      if (this.onError) {
        this.onError(error);
      } else {
        console.error('[WellRenderer] drawProfile failed:', error);
      }
    }

    const { zoom: zoomEnabled, pan: panEnabled } = this.renderConfig;
    if (zoomEnabled || panEnabled) {
      const zoomNode = d3.zoom<SVGSVGElement, unknown>();
      if (zoomEnabled || panEnabled) zoomNode.on('zoom', zooming);
      zoomNode.scaleExtent(
        zoomEnabled
          ? [
              this.renderConfig.minZoomScale ?? 0,
              this.renderConfig.maxZoomScale ?? Infinity,
            ]
          : [1, 1],
      );
      if (!panEnabled)
        zoomNode.filter(e => e.type === 'wheel' || e.type === 'dblclick');
      asSvgElement<SVGSVGElement>(svg).call(zoomNode);
      state.zoomNode = zoomNode;

      const initialZoom = this.renderConfig.zoomLevel ?? 1;
      if (initialZoom !== 1) {
        asSvgElement<SVGSVGElement>(svg).call(
          zoomNode.transform,
          d3.zoomIdentity.scale(initialZoom),
        );
      }
    }
  }
}

export default {
  WellRenderer,
};
